/**
 * Viewer Header Component
 * Common header for viewer screens with pink gradient design
 * Responsive: hamburger menu on mobile
 */

import { useState } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { LOGO_CONFIG } from '../lib/logo-config';
import { ROUTES } from '../lib/constants';
import LanguageSelector from './LanguageSelector';
import GuideLink from './GuideLink';

interface ViewerHeaderProps {
    points: number | null;
    onPointsClick: () => void;
    onExitClick?: () => void;
    showExitButton?: boolean;
    exitButtonLabel?: string;
    title?: string;
    icon?: string;
    username?: string;
    onLogout?: () => void;
    onMyPageClick?: () => void;
    onFavoritesClick?: () => void;
    onNotificationsClick?: () => void;
    notificationCount?: number;
    isAuthenticated?: boolean;
    onLoginClick?: () => void;
    onRegisterClick?: () => void;
    showLanguageSelector?: boolean;
    isAdminOrManager?: boolean;
}

export default function ViewerHeader({
    points,
    onPointsClick,
    onExitClick,
    showExitButton = false,
    exitButtonLabel,
    title,
    icon: _icon = '📺',
    username,
    onLogout,
    onMyPageClick,
    onFavoritesClick,
    onNotificationsClick,
    notificationCount = 0,
    isAuthenticated = true,
    onLoginClick,
    onRegisterClick,
    showLanguageSelector = false,
    isAdminOrManager = false,
}: ViewerHeaderProps) {
    const { t } = useTranslation('watch');
    const { t: tGuide } = useTranslation('guide');
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    // Use provided values or fallback to translated defaults
    const displayTitle = title || t('header.defaultTitle');
    const displayExitLabel = exitButtonLabel || t('header.backToList');

    return (
        <div className="bg-white text-pink-800 shadow-sm">
            <div className="max-w-[1600px] mx-auto px-2 md:px-4 py-1.5 md:py-2">
                <div className="flex items-center justify-between">
                    {/* Left: Logo, Title and Exit Button */}
                    <div className="flex items-center gap-2 md:gap-4">
                        <div className="flex items-center gap-3 md:gap-4 text-base md:text-xl font-bold text-pink-600">
                            <Link to={ROUTES.HOME} className="bg-white p-0.5 rounded hover:opacity-80 transition-opacity">
                                <img src={LOGO_CONFIG.src} alt="Live Chat" className={`${LOGO_CONFIG.heightClass} w-auto`} />
                            </Link>
                            <span className="hidden sm:inline">{displayTitle}</span>
                        </div>
                        {showExitButton && (
                            <button
                                onClick={onExitClick}
                                className="hidden sm:block px-3 md:px-4 py-1.5 md:py-2 bg-white hover:bg-pink-50 border border-pink-200 text-pink-700 transition-colors text-xs md:text-sm font-semibold"
                            >
                                {displayExitLabel}
                            </button>
                        )}
                    </div>

                    {/* Desktop Right Side */}
                    <div className="hidden md:flex items-center gap-2 lg:gap-3">
                        {isAdminOrManager ? (
                            /* Staff mode: Show only username and logout (no language selector) */
                            <>
                                {username && (
                                    <span className="px-2 py-1 text-sm text-pink-700 font-medium">
                                        {username}
                                    </span>
                                )}
                                {onLogout && (
                                    <button
                                        onClick={onLogout}
                                        className="px-3 lg:px-4 py-2 bg-white hover:bg-pink-50 border border-pink-200 text-pink-700 transition-colors text-sm font-semibold"
                                    >
                                        {t('header.logout')}
                                    </button>
                                )}
                            </>
                        ) : isAuthenticated ? (
                            <>
                                <GuideLink
                                    chapter="watch"
                                    className="px-3 lg:px-4 py-2 bg-white hover:bg-pink-50 border border-pink-200 text-pink-700 transition-colors text-sm font-semibold"
                                >
                                    {tGuide('links.watch')}
                                </GuideLink>
                                <button
                                    onClick={onPointsClick}
                                    className="flex items-center gap-2 px-3 lg:px-4 py-2 bg-white hover:bg-pink-50 border border-pink-200 text-pink-700 transition-colors text-sm font-semibold"
                                >
                                    <svg width="18" height="18" fill="currentColor" viewBox="0 0 20 20">
                                        <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z" />
                                        <path fillRule="evenodd" d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" clipRule="evenodd" />
                                    </svg>
                                    <span className="hidden lg:inline">{t('header.pointsCharge')}</span>
                                </button>

                                {points !== null && (
                                    <div className="flex items-center gap-1 px-3 py-2 text-pink-800">
                                        <span>💰</span>
                                        <span className="text-sm font-semibold">{t('header.remainingPoints')}: <strong>{points.toLocaleString()} pt</strong></span>
                                    </div>
                                )}

                                <button
                                    onClick={onFavoritesClick}
                                    className="px-3 lg:px-4 py-2 bg-white hover:bg-pink-50 border border-pink-200 text-pink-700 transition-colors text-sm font-semibold"
                                >
                                    {t('header.favorites')}
                                </button>

                                <button
                                    onClick={onNotificationsClick}
                                    className="relative w-10 h-10 flex items-center justify-center bg-white hover:bg-pink-50 border border-pink-200 text-pink-700 transition-colors"
                                    title={t('header.notifications')}
                                >
                                    <svg width="20" height="20" fill="currentColor" viewBox="0 0 20 20">
                                        <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
                                    </svg>
                                    {notificationCount > 0 && (
                                        <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full px-1.5 py-0.5 min-w-[1rem] text-center">
                                            {notificationCount}
                                        </span>
                                    )}
                                </button>

                                <button
                                    onClick={onMyPageClick}
                                    className="relative w-10 h-10 flex items-center justify-center bg-white hover:bg-pink-50 border border-pink-200 text-pink-700 transition-colors"
                                    title={t('header.myPage')}
                                >
                                    <svg width="20" height="20" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                                    </svg>
                                </button>

                                {username && (
                                    <span className="px-2 py-1 text-sm text-pink-700 font-medium">
                                        {username}
                                    </span>
                                )}

                                {onLogout && (
                                    <button
                                        onClick={onLogout}
                                        className="px-3 lg:px-4 py-2 bg-white hover:bg-pink-50 border border-pink-200 text-pink-700 transition-colors text-sm font-semibold"
                                    >
                                        {t('header.logout')}
                                    </button>
                                )}

                                {showLanguageSelector && (
                                    <LanguageSelector />
                                )}
                            </>
                        ) : (
                            <>
                                <GuideLink
                                    chapter="watch"
                                    className="px-3 lg:px-4 py-2 bg-white hover:bg-pink-50 border border-pink-200 text-pink-700 transition-colors text-sm font-semibold"
                                >
                                    {tGuide('links.watch')}
                                </GuideLink>
                                <button
                                    onClick={onLoginClick}
                                    className="px-4 lg:px-5 py-2 bg-pink-400 hover:bg-pink-500 text-white transition-colors text-sm font-bold shadow-sm"
                                >
                                    {t('header.login')}
                                </button>
                                <button
                                    onClick={onRegisterClick}
                                    className="px-4 lg:px-5 py-2 bg-white hover:bg-pink-50 border border-pink-200 text-pink-700 transition-colors text-sm font-semibold"
                                >
                                    {t('header.register')}
                                </button>
                                {showLanguageSelector && (
                                    <LanguageSelector />
                                )}
                            </>
                        )}
                    </div>

                    {/* Mobile: Points + Hamburger (or simple exit for staff) */}
                    <div className="flex md:hidden items-center gap-2">
                        {isAdminOrManager ? (
                            /* Staff mode: Show exit button and logout on mobile */
                            <>
                                {showExitButton && (
                                    <button
                                        onClick={onExitClick}
                                        className="px-3 py-1.5 bg-white hover:bg-pink-50 border border-pink-200 text-pink-700 transition-colors text-xs font-semibold"
                                    >
                                        {displayExitLabel}
                                    </button>
                                )}
                                {onLogout && (
                                    <button
                                        onClick={onLogout}
                                        className="px-3 py-1.5 bg-white hover:bg-red-50 border border-red-200 text-red-600 transition-colors text-xs font-semibold"
                                    >
                                        {t('header.logout')}
                                    </button>
                                )}
                            </>
                        ) : (
                            <>
                                {isAuthenticated && points !== null && (
                                    <div className="flex items-center gap-1 px-2 py-1 text-pink-800">
                                        <span className="text-sm">💰</span>
                                        <span className="text-xs font-semibold">{points.toLocaleString()} pt</span>
                                    </div>
                                )}
                                {!isAuthenticated ? (
                                    <>
                                        <GuideLink chapter="watch" className="px-2 py-1.5 text-xs font-semibold text-pink-700 hover:underline">
                                            {tGuide('links.watch')}
                                        </GuideLink>
                                        <button
                                            onClick={onLoginClick}
                                            className="px-3 py-1.5 bg-pink-400 hover:bg-pink-500 text-white transition-colors text-xs font-bold shadow-sm"
                                        >
                                            {t('header.login')}
                                        </button>
                                    </>
                                ) : (
                                    <button
                                        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                                        className="p-2 bg-white shadow-sm border border-pink-200"
                                        aria-label={t('header.menu')}
                                    >
                                        {isMobileMenuOpen ? (
                                            <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                        ) : (
                                            <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
                                            </svg>
                                        )}
                                    </button>
                                )}
                            </>
                        )}
                    </div>
                </div>

                {/* Mobile Menu (not shown for staff) */}
                {isMobileMenuOpen && !isAdminOrManager && (
                    <div className="md:hidden mt-3 bg-white shadow-lg border border-pink-200 overflow-hidden">
                        {/* Points Balance */}
                        {points !== null && (
                            <div className="px-4 py-3 border-b border-pink-100 flex items-center justify-between bg-pink-50">
                                <span className="text-sm text-gray-600">{t('header.remainingPoints')}</span>
                                <span className="text-sm font-bold text-pink-700">💰 {points.toLocaleString()} pt</span>
                            </div>
                        )}

                        {/* Exit Button */}
                        {showExitButton && (
                            <button
                                onClick={() => {
                                    onExitClick?.();
                                    setIsMobileMenuOpen(false);
                                }}
                                className="w-full px-4 py-3 text-left font-semibold text-sm text-gray-700 hover:bg-pink-50 transition-all border-b border-pink-100"
                            >
                                {displayExitLabel}
                            </button>
                        )}

                        {/* Guide */}
                        <GuideLink
                            chapter="watch"
                            onClick={() => setIsMobileMenuOpen(false)}
                            className="block w-full px-4 py-3 text-left font-semibold text-sm text-gray-700 hover:bg-pink-50 transition-all border-b border-pink-100"
                        >
                            {tGuide('links.watch')}
                        </GuideLink>

                        {/* Points Charge */}
                        <button
                            onClick={() => {
                                onPointsClick();
                                setIsMobileMenuOpen(false);
                            }}
                            className="w-full px-4 py-3 text-left font-semibold text-sm text-gray-700 hover:bg-pink-50 transition-all border-b border-pink-100 flex items-center gap-2"
                        >
                            <svg width="18" height="18" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z" />
                                <path fillRule="evenodd" d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" clipRule="evenodd" />
                            </svg>
                            {t('header.pointsCharge')}
                        </button>

                        {/* MyPage */}
                        <button
                            onClick={() => {
                                onMyPageClick?.();
                                setIsMobileMenuOpen(false);
                            }}
                            className="w-full px-4 py-3 text-left font-semibold text-sm text-gray-700 hover:bg-pink-50 transition-all border-b border-pink-100 flex items-center gap-2"
                        >
                            <svg width="18" height="18" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                            </svg>
                            {t('header.myPage')}
                        </button>

                        {/* Favorites */}
                        <button
                            onClick={() => {
                                onFavoritesClick?.();
                                setIsMobileMenuOpen(false);
                            }}
                            className="w-full px-4 py-3 text-left font-semibold text-sm text-gray-700 hover:bg-pink-50 transition-all border-b border-pink-100 flex items-center gap-2"
                        >
                            <svg width="18" height="18" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                            </svg>
                            {t('header.favorites')}
                        </button>

                        {/* Notifications */}
                        <button
                            onClick={() => {
                                onNotificationsClick?.();
                                setIsMobileMenuOpen(false);
                            }}
                            className="w-full px-4 py-3 text-left font-semibold text-sm text-gray-700 hover:bg-pink-50 transition-all border-b border-pink-100 flex items-center justify-between"
                        >
                            <div className="flex items-center gap-2">
                                <svg width="18" height="18" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
                                </svg>
                                {t('header.notifications')}
                            </div>
                            {notificationCount > 0 && (
                                <span className="bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                                    {notificationCount}
                                </span>
                            )}
                        </button>

                        {/* Language Selector in mobile menu */}
                        {showLanguageSelector && (
                            <div className="px-4 py-3 border-t border-pink-100 flex items-center justify-between">
                                <span className="text-sm text-gray-600">{t('header.language')}</span>
                                <LanguageSelector />
                            </div>
                        )}

                        {/* Username and Logout in mobile menu */}
                        {username && (
                            <div className="px-4 py-3 border-t border-pink-100 flex items-center gap-2 bg-pink-50">
                                <svg width="18" height="18" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                                </svg>
                                <span className="text-sm font-semibold text-pink-700">{username}</span>
                            </div>
                        )}

                        {onLogout && (
                            <button
                                onClick={() => {
                                    onLogout();
                                    setIsMobileMenuOpen(false);
                                }}
                                className="w-full px-4 py-3 text-left font-semibold text-sm text-red-600 hover:bg-red-50 transition-all border-t border-pink-100 flex items-center gap-2"
                            >
                                <svg width="18" height="18" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M3 3a1 1 0 00-1 1v12a1 1 0 102 0V4a1 1 0 00-1-1zm10.293 9.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L14.586 9H7a1 1 0 100 2h7.586l-1.293 1.293z" clipRule="evenodd" />
                                </svg>
                                {t('header.logout')}
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
